import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import './style.css';

const ContactForm = () => {
  const formRef = useRef();
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nom.trim()) {
      newErrors.nom = "Last name is required";
    }
    
    if (!formData.prenom.trim()) {
      newErrors.prenom = "First name is required";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    
    if (formData.telephone && !/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/.test(formData.telephone)) {
      newErrors.telephone = "Invalid phone format";
    }
    
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Your message must be at least 10 characters long";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setSubmitStatus('loading');
      
      // EmailJS configuration
      const serviceId = 'service_dpnykzp'; // Replace with your EmailJS service ID
      const templateId = 'template_l93ndag'; // Replace with your EmailJS template ID
      const publicKey = 'GWwKkijVfnVIBgviu'; // Replace with your EmailJS public key
      
      // Send email using EmailJS
      emailjs.sendForm(serviceId, templateId, formRef.current, publicKey)
        .then((result) => {
          console.log('Email sent successfully:', result.text);
          setSubmitStatus('success');
          
          // Reset form after successful submission
          setFormData({
            nom: '',
            prenom: '',
            email: '',
            telephone: '',
            message: ''
          });
          
          // Reset success message after 5 seconds
          setTimeout(() => {
            setSubmitStatus(null);
          }, 5000);
        })
        .catch((error) => {
          console.error('Email send error:', error.text);
          setSubmitStatus('error');
          
          // Reset error message after 5 seconds
          setTimeout(() => {
            setSubmitStatus(null);
          }, 5000);
        });
    }
  };

  return (
    <div className="contact-container">
      <div className="contact-header">
        <h1>Contact Us</h1>
        <p>We are here to answer your questions and support you with your IT projects.</p>
      </div>
      
      <div className="contact-content">
        <div className="contact-info">
          <div className="info-item">
            <i className="fas fa-map-marker-alt"></i>
            <div>
              <h3>Address</h3>
              <p className="info-item-p">Avenue Pasteur 6H,</p>
              <p className="info-item-p">1300 Wavre, Belgium</p>
            </div>
          </div>
          
          <div className="info-item">
            <i className="fas fa-phone-alt"></i>
            <div>
              <h3>Phone</h3>
              <p className="info-item-p">+32 484 666 560</p>
            </div>
          </div>
          
          <div className="info-item">
            <i className="fas fa-envelope"></i>
            <div>
              <h3>Email</h3>
              <p className="info-item-p">contact@techstride.be</p>
            </div>
          </div>
          
          <div className="info-item">
            <i className="fas fa-clock"></i>
            <div>
              <h3>Hours</h3>
              <p className="info-item-p">Monday - Friday: 9 AM to 6 PM</p>
            </div>
          </div>
          
          <div className="social-links">
            <h3>Follow us</h3>
            <div className="social-icons">
              <a href="https://www.linkedin.com/company/tech-stride-consulting/?viewAsMember=true" aria-label="LinkedIn"><i className="fab fa-linkedin"></i></a>
              <a href="https://www.facebook.com/techstrideconsulting" aria-label="Facebook"><i className="fab fa-facebook"></i></a>
            </div>
          </div>
        </div>
        
        <div className="contact-form-container">
          <h2>Send us a message</h2>
          <form ref={formRef} className="contact-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nom">Last Name*</label>
                <input
                  type="text"
                  id="nom"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  className={errors.nom ? 'error' : ''}
                />
                {errors.nom && <span className="error-message">{errors.nom}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="prenom">First Name*</label>
                <input
                  type="text"
                  id="prenom"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  className={errors.prenom ? 'error' : ''}
                />
                {errors.prenom && <span className="error-message">{errors.prenom}</span>}
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email*</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="telephone">Phone</label>
                <input
                  type="tel"
                  id="telephone"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  className={errors.telephone ? 'error' : ''}
                />
                {errors.telephone && <span className="error-message">{errors.telephone}</span>}
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="message">Message*</label>
              <textarea
                id="message"
                name="message"
                rows="5"
                value={formData.message}
                onChange={handleChange}
                className={errors.message ? 'error' : ''}
              ></textarea>
              {errors.message && <span className="error-message">{errors.message}</span>}
            </div>
            
            <div className="form-group submission">
              <button 
                type="submit" 
                className={`submit-btn ${submitStatus === 'loading' ? 'loading' : ''}`}
                disabled={submitStatus === 'loading'}
              >
                {submitStatus === 'loading' ? (
                  <>
                    <span className="spinner"></span>
                    Sending...
                  </>
                ) : 'Send Message'}
              </button>
              
              {submitStatus === 'success' && (
                <div className="success-message">
                  <i className="fas fa-check-circle"></i>
                  Your message has been sent successfully. We will get back to you as soon as possible.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i>
                  An error occurred while sending your message. Please try again or contact us directly.
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
